import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import Select from "react-select";
import useDarkMode from "../../hooks/useDarkMode";
import DropDownStyles, {
  DropDownStylesDark,
  defaultSelectPortalProps,
} from "../staff/dropdownStyles";
import useThemeColor from "../../hooks/useThemeColor";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import moment from "moment";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import "./TableCoverReportPage.css";
import {
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import ModernGridToolbar, {
  ModernGridToolbarFilterPill,
} from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import { getModernGridFilterPillSelectStyles } from "../../components/CustomComponents/ModernGridToolbar/modernGridToolbarSelectStyles";

const normalizeCurrencyCode = (currencyCode) => {
  const map = {
    "£": "GBP",
    "€": "EUR",
    "€ ": "EUR",
    $: "USD",
    "₹": "INR",
    "₨": "PKR",
    Rs: "PKR",
  };

  if (!currencyCode) return "GBP";
  const trimmed = currencyCode.trim();
  if (map[trimmed]) return map[trimmed];
  if (/^[A-Za-z]{3}$/.test(trimmed)) return trimmed.toUpperCase();
  return "GBP";
};

const currencyFormatter = (currencyCode) => {
  const safeCode = normalizeCurrencyCode(currencyCode);
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: safeCode,
    minimumFractionDigits: 2,
  });
};

const formatNumber = (value) => new Intl.NumberFormat().format(value ?? 0);

const TABLE_COVER_SORT_FIELDS = {
  tableName: "tableName",
  serviceArea: "serviceAreaName",
  coverCount: "coverCount",
  totalAmount: "totalAmount",
};

const TABLE_COVER_SORT_LABELS = {
  [TABLE_COVER_SORT_FIELDS.tableName]: "Table",
  [TABLE_COVER_SORT_FIELDS.serviceArea]: "Service Area",
  [TABLE_COVER_SORT_FIELDS.coverCount]: "Covers",
  [TABLE_COVER_SORT_FIELDS.totalAmount]: "Total Amount",
};

const getSortValue = (row, field) => {
  const val = row[field] ?? row[field.charAt(0).toUpperCase() + field.slice(1)];
  if (field === "tableName" || field === "serviceAreaName") {
    return (val ?? "").toString().toLowerCase();
  }
  return Number(val) || 0;
};

const TableCoverReport = () => {
  const CompID = useSelector((state) => state.auth.user?.userData?.compID);
  const dispatch = useDispatch();
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const dropdownStyles = useMemo(
    () => (isDarkMode ? DropDownStylesDark : DropDownStyles),
    [isDarkMode]
  );
  const filterPillSelectStyles = useMemo(
    () => getModernGridFilterPillSelectStyles(dropdownStyles, isDarkMode),
    [dropdownStyles, isDarkMode]
  );
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = normalizeCurrencyCode(getCompCurrency());
  const currency = useMemo(() => currencyFormatter(compCurrency), [compCurrency]);

  const pageKey = "tableCoverReport";
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: TABLE_COVER_SORT_FIELDS.totalAmount, label: "Total Amount" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;

  const [serviceAreas, setServiceAreas] = useState([
    { value: "all", label: "All Service Areas" },
  ]);
  const [selectedArea, setSelectedArea] = useState(serviceAreas[0]);
  const [rangeDates, setRangeDates] = useState([
    moment().startOf("day").toDate(),
    moment().endOf("day").toDate(),
  ]);
  const [summary, setSummary] = useState(null);
  const [tableDetails, setTableDetails] = useState([]);
  const [orderDetails, setOrderDetails] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [error, setError] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [expandedTableId, setExpandedTableId] = useState(null);
  const reportFetchSeqRef = useRef(0);

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: TABLE_COVER_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const clearFilters = () => {
    setRangeDates([
      moment().startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ]);
    setSelectedArea(serviceAreas[0]);
  };

  const hasFiltersApplied = () => {
    const todayStart = moment().startOf("day");
    const todayEnd = moment().endOf("day");
    const rangeChanged =
      !moment(rangeDates[0]).isSame(todayStart, "day") ||
      !moment(rangeDates[1] || rangeDates[0]).isSame(todayEnd, "day");
    return rangeChanged || selectedArea?.value !== "all";
  };

  const fetchServiceAreas = useCallback(async () => {
    if (!CompID) return;
    try {
      const response = await axiosInstance.get(APIs.GET_SERVICE_AREAS, {
        params: { CompID, Search: "" },
      });
      const raw = response.data?.data || response.data || [];
      const mapped = Array.isArray(raw)
        ? raw.map((area) => ({
            value:
              area.id ?? area.ID ?? area.sA_ID ?? area.sa_ID ?? area.SA_ID,
            label: area.sA_Name ?? area.SA_Name ?? area.name ?? "Service Area",
          }))
        : [];
      const unique = mapped.filter((option) => option.value);
      const options = [{ value: "all", label: "All Service Areas" }, ...unique];
      setServiceAreas(options);
      setSelectedArea((prev) =>
        options.find((opt) => opt.value === prev?.value) || options[0]
      );
    } catch (err) {
      console.error("Failed to load service areas", err);
    }
  }, [CompID]);

  const fetchReport = useCallback(async () => {
    if (!CompID) return;
    const fetchSeq = ++reportFetchSeqRef.current;
    setLoading(true);
    setError("");
    try {
      const startDate = rangeDates[0];
      const endDate = rangeDates[1] || rangeDates[0];

      const params = { CompID };

      if (startDate) {
        params.StartDate = moment(startDate).format("YYYY-MM-DD");
      }
      if (endDate) {
        params.EndDate = moment(endDate).format("YYYY-MM-DD");
      }
      if (selectedArea?.value && selectedArea.value !== "all") {
        params.ServiceAreaID = selectedArea.value;
      }

      const response = await axiosInstance.get(APIs.GET_COVERS_SPENT_REPORT, { params });

      if (fetchSeq !== reportFetchSeqRef.current) return;

      if (response.data?.status) {
        const payload = response.data.data || response.data || {};
        setSummary(payload.summary || payload.Summary || null);
        const tables = payload.tables || payload.Tables || [];
        const orders = payload.orders || payload.Orders || [];
        const normalizedTables = Array.isArray(tables)
          ? tables.map((table) => ({
              ...table,
              tableId: table.tableId || table.TableId,
              tableName: table.tableName || table.TableName,
              serviceAreaName: table.serviceAreaName || table.ServiceAreaName,
              coverCount: table.coverCount ?? table.CoverCount,
              totalAmount: table.totalAmount ?? table.TotalAmount,
            }))
          : [];
        setTableDetails(normalizedTables);
        setOrderDetails(Array.isArray(orders) ? orders : []);
      } else {
        setSummary(null);
        setTableDetails([]);
        setOrderDetails([]);
        setError(
          response.data?.message || "Unable to fetch covers spent report."
        );
      }
    } catch (err) {
      if (fetchSeq !== reportFetchSeqRef.current) return;
      console.error(err);
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Unable to fetch covers spent report."
      );
      setSummary(null);
      setTableDetails([]);
      setOrderDetails([]);
    } finally {
      if (fetchSeq === reportFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [CompID, rangeDates, selectedArea]);

  useEffect(() => {
    fetchServiceAreas();
  }, [fetchServiceAreas]);

  useDebouncedEffect(() => fetchReport(), [fetchReport]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeDates, selectedArea?.value]);

  useEffect(() => {
    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useResetPageOnFilterChange(setPageNumber, [
    rangeDates,
    selectedArea?.value,
    pageSize?.value,
    sortBy?.value,
    sortOrder?.value,
  ]);

  const sortedTables = useMemo(() => {
    const rows = [...tableDetails];
    const field = sortBy?.value;
    const dir = sortOrder?.value === "ASC" ? 1 : -1;
    if (!field) return rows;
    return rows.sort((a, b) => {
      const av = getSortValue(a, field);
      const bv = getSortValue(b, field);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [tableDetails, sortBy?.value, sortOrder?.value]);

  const totalRecords = sortedTables.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / Number(pageSize.value)));
  const pagedTables = useMemo(() => {
    const size = Number(pageSize.value);
    const start = (pageNumber - 1) * size;
    return sortedTables.slice(start, start + size);
  }, [sortedTables, pageNumber, pageSize]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const handleRangeChange = (dates) => {
    const [start, end] = dates || [];
    if (!start) return;
    setRangeDates([start, end || start]);
  };

  const getOrdersForTable = (tableName) =>
    orderDetails.filter(
      (order) => (order.tableName || order.TableName) === tableName
    );

  const renderSummary = () => {
    if (!summary) return null;

    const cards = [
      {
        icon: "fa-users",
        label: "Total Covers",
        value: formatNumber(summary.totalCovers || summary.TotalCovers || 0),
      },
      {
        icon: "fa-coins",
        label: "Total Covers Spent",
        value: currency.format(
          summary.totalCoversSpent || summary.TotalCoversSpent || 0
        ),
      },
      {
        icon: "fa-divide",
        label: "Avg Spend / Cover",
        value: currency.format(
          summary.averageCoverSpent || summary.AverageCoverSpent || 0
        ),
      },
    ];

    return (
      <div
        className="mb-4 px-3 pt-3"
        style={{ position: "relative", zIndex: 0, isolation: "isolate" }}
      >
        <div className="d-flex gap-3 flex-wrap">
          {cards.map((card) => (
            <div
              key={card.label}
              className="flex-grow-1"
              style={{
                minWidth: "170px",
                backgroundColor: themeColor,
                borderRadius: "8px",
                padding: "16px 20px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                color: "white",
              }}
            >
              <i
                className={`fa ${card.icon}`}
                style={{ fontSize: "22px", opacity: 0.9 }}
              />
              <div>
                <div style={{ fontSize: "20px", fontWeight: "bold" }}>
                  {card.value}
                </div>
                <div style={{ fontSize: "12px", opacity: 0.85 }}>{card.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTableOrders = (tableOrders) => {
    if (!tableOrders.length) return null;

    return (
      <div className="table-orders-section mt-2">
        <div className="orders-header">
          <small className="text-muted fw-semibold">
            <i className="fa fa-receipt me-1" />
            Orders ({tableOrders.length}) — click an order to view seat details
          </small>
        </div>
        <div className="orders-grid">
          {tableOrders.map((order, idx) => (
            <div
              key={`${order.orderNumber || order.OrderNumber}-${idx}`}
              className="order-card"
              onClick={() => setSelectedOrder(order)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  setSelectedOrder(order);
                }
              }}
            >
              <div className="order-card-row">
                <div className="order-card-col">
                  <div className="order-card-label">Order #</div>
                  <div className="order-card-value">
                    {(order.orderNumber || order.OrderNumber || "N/A").substring(0, 20)}
                    {(order.orderNumber || order.OrderNumber || "").length > 20
                      ? "..."
                      : ""}
                  </div>
                </div>
                <div className="order-card-col">
                  <div className="order-card-label">
                    <i className="fa fa-calendar-alt me-1" />
                    Date & Time
                  </div>
                  <div className="order-card-value">
                    {order.orderTime || order.OrderTime
                      ? moment(order.orderTime || order.OrderTime).format("MMM DD, YYYY")
                      : "N/A"}
                    <br />
                    <small className="text-muted">
                      {order.orderTime || order.OrderTime
                        ? moment(order.orderTime || order.OrderTime).format("hh:mm A")
                        : ""}
                    </small>
                  </div>
                </div>
                <div className="order-card-col">
                  <div className="order-card-label">
                    <i className="fa fa-users me-1" />
                    Seats
                  </div>
                  <div className="order-card-value">
                    <span className="seat-badge-large">
                      {formatNumber(order.seatCount || order.SeatCount || 0)}
                    </span>
                  </div>
                </div>
                <div className="order-card-col">
                  <div className="order-card-label">
                    <i className="fa fa-money-bill-wave me-1" />
                    Amount
                  </div>
                  <div className="order-card-value order-amount-large">
                    {currency.format(order.totalAmount || order.TotalAmount || 0)}
                  </div>
                </div>
              </div>
              <div className="order-card-footer">
                <button
                  type="button"
                  className="view-details-btn-large"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedOrder(order);
                  }}
                  title="View seat details"
                >
                  <i className="fa fa-eye me-1" />
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTableGrid = () => {
    const sortFields = TABLE_COVER_SORT_FIELDS;
    const colSpan = 6;

    return (
      <div
        className="table-responsive"
        style={{ minHeight: "200px", maxHeight: "450px", overflow: "auto" }}
      >
        <table
          className="table table-hover"
          style={{ "--modern-grid-sort-active": themeColor }}
        >
          <thead
            className="bg-light"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
              color: isDarkMode ? "#ffffff" : "#000000",
            }}
          >
            <tr>
              <th className="pt-2 pb-2" style={{ width: 40 }} />
              <SortableColumnHeader
                sortField={sortFields.tableName}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleColumnSort}
              >
                Table
              </SortableColumnHeader>
              <SortableColumnHeader
                sortField={sortFields.serviceArea}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleColumnSort}
              >
                Service Area
              </SortableColumnHeader>
              <SortableColumnHeader
                sortField={sortFields.coverCount}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleColumnSort}
                className="text-center"
              >
                Covers
              </SortableColumnHeader>
              <SortableColumnHeader
                sortField={sortFields.totalAmount}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleColumnSort}
                className="text-end"
              >
                Total Amount
              </SortableColumnHeader>
              <th className="pt-2 pb-2 text-center">Orders</th>
            </tr>
          </thead>
          <tbody>
            {pagedTables.map((table) => {
              const tableId = table.tableId;
              const tableName = table.tableName;
              const tableOrders = getOrdersForTable(tableName);
              const isExpanded = expandedTableId === tableId;

              return (
                <React.Fragment key={tableId}>
                  <tr>
                    <td className="align-middle">
                      {tableOrders.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0"
                          onClick={() =>
                            setExpandedTableId(isExpanded ? null : tableId)
                          }
                        >
                          <i className={`fa fa-chevron-${isExpanded ? "down" : "right"}`} />
                        </button>
                      )}
                    </td>
                    <td className="align-middle fw-semibold">{tableName}</td>
                    <td className="align-middle">
                      {table.serviceAreaName || "Unknown Area"}
                    </td>
                    <td className="align-middle text-center">
                      {formatNumber(table.coverCount)}
                    </td>
                    <td className="align-middle text-end fw-semibold">
                      {currency.format(table.totalAmount || 0)}
                    </td>
                    <td className="align-middle text-center">
                      {formatNumber(tableOrders.length)}
                    </td>
                  </tr>
                  {isExpanded && tableOrders.length > 0 && (
                    <tr>
                      <td colSpan={colSpan} className="bg-light bg-opacity-50">
                        {renderTableOrders(tableOrders)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSeatModal = () => {
    if (!selectedOrder) return null;

    const seats = selectedOrder.seats || selectedOrder.Seats || [];

    return (
      <div
        className="modal fade show"
        style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        role="presentation"
      >
        <div
          className="modal-dialog modal-lg modal-dialog-centered"
          onClick={(e) => e.stopPropagation()}
          style={{ zIndex: 1051 }}
          role="document"
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fa fa-receipt me-2" />
                Order {selectedOrder.orderNumber || selectedOrder.OrderNumber} - Seat Details
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setSelectedOrder(null)}
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div className="row mb-3">
                <div className="col-md-4">
                  <strong>Table:</strong>
                  <br />
                  <span className="text-muted">
                    {selectedOrder.tableName || selectedOrder.TableName || "N/A"}
                  </span>
                </div>
                <div className="col-md-4">
                  <strong>Order Time:</strong>
                  <br />
                  <span className="text-muted">
                    {selectedOrder.orderTime || selectedOrder.OrderTime
                      ? moment(selectedOrder.orderTime || selectedOrder.OrderTime).format(
                          "MMM DD, YYYY hh:mm A"
                        )
                      : "N/A"}
                  </span>
                </div>
                <div className="col-md-4">
                  <strong>Total Amount:</strong>
                  <br />
                  <span className="text-primary fw-bold">
                    {currency.format(
                      selectedOrder.totalAmount || selectedOrder.TotalAmount || 0
                    )}
                  </span>
                </div>
              </div>
              <hr />
              <h6 className="mb-3">
                <i className="fa fa-users me-2" />
                Seat Details ({seats.length} {seats.length === 1 ? "seat" : "seats"})
              </h6>
              {seats.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead>
                      <tr>
                        <th>Seat Number</th>
                        <th>Seat Name</th>
                        <th className="text-end">Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {seats.map((seat, idx) => (
                        <tr key={idx}>
                          <td>
                            <strong>{seat.seatNumber || seat.SeatNumber || "N/A"}</strong>
                          </td>
                          <td>{seat.seatName || seat.SeatName || "-"}</td>
                          <td className="text-end">
                            {currency.format(seat.amount || seat.Amount || 0)}
                          </td>
                          <td>
                            {seat.isReserved || seat.IsReserved ? (
                              <span className="badge bg-warning">Reserved</span>
                            ) : seat.isVIP || seat.IsVIP ? (
                              <span className="badge bg-info">VIP</span>
                            ) : (
                              <span className="badge bg-success">Active</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th colSpan="2" className="text-end">
                          Total:
                        </th>
                        <th className="text-end">
                          {currency.format(
                            seats.reduce(
                              (sum, seat) => sum + (seat.amount || seat.Amount || 0),
                              0
                            )
                          )}
                        </th>
                        <th />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="alert alert-info">
                  <i className="fa fa-info-circle me-2" />
                  No seat details available for this order.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setSelectedOrder(null)}
              >
                <i className="fa fa-times me-1" />
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const listEmpty =
    initialLoadComplete && !loading && tableDetails.length === 0;

  const getEmptyStateDescription = () => {
    if (hasFiltersApplied()) {
      return "No data matches the selected date range or service area. Try adjusting filters or clear them.";
    }
    return "No table cover data is available for the selected period.";
  };

  return (
    <div className={`table-cover-page ${isDarkMode ? "dark-mode" : ""}`}>
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item">
          <a href="#/">Home</a>
        </li>
        <li className="breadcrumb-item">
          <a href="#/report">Reports</a>
        </li>
        <li className="breadcrumb-item active">Table Cover Report</li>
      </ol>
      <h1 className="page-header">Table Cover Report</h1>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {!initialLoadComplete && (
        <GridSectionLoader
          loading
          minHeight="320px"
          message="Fetching covers spent report..."
        />
      )}

      {initialLoadComplete && (
        <ModernGridToolbar
          isDarkMode={isDarkMode}
          title="Tables"
          totalCount={totalRecords}
          bodyFlush
          topFilters={
            <>
              <ModernGridToolbarFilterPill
                iconClass="fa fa-table"
                className="modern-grid-toolbar__filter-pill--status"
              >
                <Select
                  value={selectedArea}
                  onChange={(option) => setSelectedArea(option)}
                  options={serviceAreas}
                  styles={filterPillSelectStyles}
                  placeholder="All Service Areas"
                  isSearchable={false}
                  {...defaultSelectPortalProps}
                />
              </ModernGridToolbarFilterPill>
              <ModernGridToolbarFilterPill iconClass="fa fa-calendar" wide>
                <DateRangePicker
                  startDate={rangeDates[0]}
                  endDate={rangeDates[1]}
                  onChange={handleRangeChange}
                  placeholder="Select Date Range"
                />
              </ModernGridToolbarFilterPill>
            </>
          }
        >
          {renderSummary()}

          <div className="px-3 pb-3">
            <p className="text-muted small mb-2">
              Expand a table to view orders; click an order to view seat details.
            </p>
            <GridSectionLoader
              loading={loading}
              minHeight="280px"
              message="Fetching covers spent report..."
            >
              {listEmpty ? (
                <GridEmptyState
                  isDarkMode={isDarkMode}
                  themeColor={themeColor}
                  title="No data found"
                  description={getEmptyStateDescription()}
                  onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
                  clearFiltersLabel="Clear all filters"
                />
              ) : (
                <>
                  {renderTableGrid()}
                  <ModernGridPagerFooter
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    totalRecords={totalRecords}
                    pageNumber={pageNumber}
                    totalPages={totalPages}
                    setPageNumber={setPageNumber}
                  />
                </>
              )}
            </GridSectionLoader>
          </div>
        </ModernGridToolbar>
      )}

      {renderSeatModal()}
    </div>
  );
};

export default TableCoverReport;
